import { useEffect, useState } from 'react';
import {
  useAnalyzeKsActMutation,
  useCreateSmetaRequestMutation,
  useDeleteKsActMutation,
  useGetKsActQuery,
  useLazyExportKsCheckXlsxQuery,
  useListKsActsQuery,
} from '@shared/api';
import { Card } from '@shared/ui/Card';
import { Button } from '@shared/ui/Button';
import { Badge } from '@shared/ui/Badge';
import { useAgentPermission } from '@entities/user';
import { useAppSelector } from '@app/hooks';
import { ParseWarningsBanner } from '../ParseWarningsBanner';
import { ActItemsTable } from './ActItemsTable';
import { ActUploadCard } from './ActUploadCard';
import { BenchmarkUploadCard } from './BenchmarkUploadCard';
import { DiscrepanciesTable } from './DiscrepanciesTable';
import { actKindLabel, statusLabel, statusTone } from './ksCheckLabels';
import {
  CheckRow,
  ErrMargin,
  Grid,
  Muted,
  MutedMargin,
  ObjName,
  Row,
  Stack,
  TableTitle,
} from './styled';
import { RegistryPager, useRegistryPage, useSelectedRecordParam } from '../registryPaging';

export function KsChecksView() {
  const [contractId, setContractId] = useState('');
  const [selectedId, setSelectedId] = useSelectedRecordParam();
  const [poll, setPoll] = useState(0);
  const [exportError, setExportError] = useState<string | null>(null);
  const [requestNotice, setRequestNotice] = useState<string | null>(null);

  const { can_write } = useAgentPermission('smeta');
  const currentUserId = useAppSelector((s) => s.auth.user?.id);

  const page = useRegistryPage();
  const listQ = useListKsActsQuery(
    { limit: page.limit, offset: page.offset },
    { pollingInterval: 5000 },
  );
  const [deleteAct] = useDeleteKsActMutation();
  const [analyze, { isLoading: analyzing }] = useAnalyzeKsActMutation();
  const [createRequest, { isLoading: sendingRequest }] = useCreateSmetaRequestMutation();
  const detailQ = useGetKsActQuery(selectedId ?? '', { skip: !selectedId, pollingInterval: poll });
  const [exportXlsx, { isFetching: exporting }] = useLazyExportKsCheckXlsxQuery();

  useEffect(() => {
    if (!selectedId) {
      setPoll(0);
      return;
    }
    const st = detailQ.data?.act.status;
    setPoll(st === 'pending' || st === 'analyzing' ? 2500 : 0);
  }, [selectedId, detailQ.data?.act.status]);

  async function handleAnalyze(id: string) {
    // Повторный анализ начинается с clear_for_act — стирает ВСЕ HITL-решения по
    // акту. Для уже проверенного акта спрашиваем подтверждение.
    if (
      detail?.act.id === id &&
      detail.act.status === 'analyzed' &&
      !window.confirm(
        'Повторная проверка удалит все ваши решения по расхождениям (принято / спорно / отклонено). Продолжить?',
      )
    ) {
      return;
    }
    try {
      await analyze(id).unwrap();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('analyzeKsAct failed', err);
    }
  }

  async function handleDelete(id: string) {
    try {
      await deleteAct(id).unwrap();
      if (selectedId === id) setSelectedId(null);
    } catch {
      // ignore — список обновится при поллинге
    }
  }

  async function handleExport(id: string) {
    setExportError(null);
    try {
      const blob = await exportXlsx(id).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ks_check_${id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError('Не удалось выгрузить отчёт сверки.');
      // eslint-disable-next-line no-console
      console.error('exportKsCheckXlsx failed', err);
    }
  }

  async function handleSendDsRequest() {
    if (!detail) return;
    setRequestNotice(null);
    setExportError(null);
    const actLabel = `${actKindLabel(detail.act.kind)}${detail.act.act_no ? ` № ${detail.act.act_no}` : ''}`;
    try {
      await createRequest({
        kind: 'contract_price_revision',
        contract_id: detail.act.contract_id ?? undefined,
        project_id: detail.act.project_id ?? undefined,
        title: `Подготовка ДС по сверке ${actLabel}`,
        summary: `Сметно-ценовой отдел проверил ${actLabel} (${detail.items.length} поз., расхождений: ${detail.discrepancies.length}). Выявлены объёмы сверх договора / расхождения расценок — требуется дополнительное соглашение.`,
        details: {
          ks_act_id: detail.act.id,
          act_no: detail.act.act_no,
          kind: detail.act.kind,
          discrepancy_count: detail.discrepancies.length,
        },
      }).unwrap();
      setRequestNotice('Заявка на подготовку ДС отправлена в Юриста (Статут) / Экономиста.');
    } catch {
      setExportError('Не удалось отправить заявку на ДС.');
    }
  }

  const acts = listQ.data?.items ?? [];
  const detail = detailQ.data;
  const isAuthor =
    !detail?.act.user_id ||
    !currentUserId ||
    detail.act.user_id === currentUserId;
  const canAnalyze =
    !!detail &&
    !!detail.act.contract_estimate_id &&
    ['parsed', 'analyzed', 'analyze_failed'].includes(detail.act.status);

  return (
    <Stack>
      <Grid>
        <BenchmarkUploadCard
          contractId={contractId}
          setContractId={setContractId}
          canWrite={can_write}
        />
        <ActUploadCard
          contractId={contractId}
          canWrite={can_write}
          onUploaded={(actId) => setSelectedId(actId)}
        />
      </Grid>

      <Grid>
        <Stack>
          <RegistryPager
            label="Актов"
            page={page}
            shown={acts.length}
            hasMore={listQ.data?.has_more}
            fetching={listQ.isFetching}
          />
          {acts.map((a) => (
            <CheckRow key={a.id} $active={a.id === selectedId} onClick={() => setSelectedId(a.id)}>
              <Row>
                <ObjName>
                  {actKindLabel(a.kind)}
                  {a.act_no ? ` № ${a.act_no}` : ''}
                </ObjName>
                <Badge $tone={statusTone(a.status)}>{statusLabel(a.status)}</Badge>
              </Row>
              <Row>
                <Muted>
                  {a.source_filename} · {new Date(a.created_at).toLocaleDateString('ru-RU')}
                </Muted>
              </Row>
            </CheckRow>
          ))}
          {acts.length === 0 && <Muted>Пока нет актов. Загрузите пакет «выполнение» выше.</Muted>}
        </Stack>

        <div>
          {!selectedId && <Muted>Выберите акт слева, чтобы увидеть сверку с договорной сметой.</Muted>}
          {selectedId && !detail && <Muted>Загрузка…</Muted>}
          {detail && (
            <Card>
              <Row>
                <div>
                  <ObjName>
                    {actKindLabel(detail.act.kind)}
                    {detail.act.act_no ? ` № ${detail.act.act_no}` : ''}
                  </ObjName>
                  <Muted>
                    {detail.act.source_filename} · позиций: {detail.items.length} · находок:{' '}
                    {detail.discrepancies.length}
                    {detail.act.contract_estimate_id ? '' : ' · эталон не привязан'}
                  </Muted>
                </div>
                <Row>
                  <Badge $tone={statusTone(detail.act.status)}>
                    {statusLabel(detail.act.status)}
                  </Badge>
                  {canAnalyze && (
                    <Button
                      $variant="primary"
                      $size="sm"
                      type="button"
                      onClick={() => handleAnalyze(detail.act.id)}
                      disabled={analyzing || !can_write}
                      title={!can_write ? 'Только чтение' : undefined}
                    >
                      {analyzing ? 'Запуск…' : 'Проверить'}
                    </Button>
                  )}
                  {detail.act.status === 'analyzed' && (
                    <Button
                      $variant="secondary"
                      $size="sm"
                      type="button"
                      onClick={() => handleExport(detail.act.id)}
                      disabled={exporting}
                    >
                      {exporting ? 'Готовлю…' : 'Отчёт (XLSX)'}
                    </Button>
                  )}
                  {detail.act.status === 'analyzed' && detail.discrepancies.length > 0 && (
                    <Button
                      $variant="secondary"
                      $size="sm"
                      type="button"
                      disabled={sendingRequest || !can_write}
                      title="Инициировать дополнительное соглашение в Статут / Экономиста"
                      onClick={handleSendDsRequest}
                    >
                      {sendingRequest ? 'Отправляю…' : 'Заявка на ДС (Юристу)'}
                    </Button>
                  )}
                  <Button
                    $variant="ghost"
                    $size="sm"
                    type="button"
                    disabled={!can_write || !isAuthor}
                    title={
                      !can_write
                        ? 'Только чтение'
                        : !isAuthor
                          ? 'Удалить акт может только его автор'
                          : undefined
                    }
                    onClick={() => handleDelete(detail.act.id)}
                  >
                    Удалить
                  </Button>
                </Row>
              </Row>

              {requestNotice && <MutedMargin>{requestNotice}</MutedMargin>}
              <ParseWarningsBanner warnings={detail.warnings} />
              {!detail.act.contract_estimate_id && (
                <ErrMargin>
                  Эталон не привязан к акту — анализ невозможен. Загрузите акт заново, выбрав
                  договорную смету при загрузке.
                </ErrMargin>
              )}
              {(detail.act.status === 'failed' || detail.act.status === 'analyze_failed') &&
                detail.act.error_message && (
                  <ErrMargin>{detail.act.error_message}</ErrMargin>
                )}
              {exportError && <ErrMargin>{exportError}</ErrMargin>}

              <TableTitle>
                Расхождения <Badge $tone="neutral">{detail.discrepancies.length}</Badge>
              </TableTitle>
              <DiscrepanciesTable items={detail.discrepancies} actId={detail.act.id} />

              <TableTitle>
                Позиции акта <Badge $tone="neutral">{detail.items.length}</Badge>
              </TableTitle>
              <ActItemsTable items={detail.items} />
            </Card>
          )}
        </div>
      </Grid>
    </Stack>
  );
}
